import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authRequired, setAuthRequired] = useState(false);
  const [unauthorized, setUnauthorized] = useState(false);

  const checkAuthStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/auth/status`, {
        credentials: 'include', // Important: send cookies
      });
      const data = await response.json();
      
      setAuthRequired(data.auth_required);
      
      if (data.unauthorized) {
        setUnauthorized(true);
        setUser(null);
      } else if (data.authenticated && data.user) {
        setUser(data.user);
        setUnauthorized(false);
      } else {
        setUser(null);
        setUnauthorized(false);
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check for unauthorized error in URL params
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    
    if (error === 'unauthorized') {
      setUnauthorized(true);
      setLoading(false);
      // Clear the error from URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    
    checkAuthStatus();
  }, [checkAuthStatus]);

  const login = async () => {
    try {
      // Remember the deep-link URL so we can restore it after Azure redirects us back to `/`.
      const here = window.location.pathname + window.location.search;
      if (here && here !== '/' && here !== '/?') {
        sessionStorage.setItem('auth_return_to', here);
      }

      // Get the auth URL from backend
      const response = await fetch(`${API_URL}/auth/login`, {
        credentials: 'include',
      });
      const data = await response.json();

      if (data.success && data.auth_url) {
        // Redirect to Azure AD login
        window.location.href = data.auth_url;
      } else {
        throw new Error('Failed to get auth URL');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Failed to initiate login');
    }
  };

  const logout = async () => {
    try {
      const response = await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        setUser(null);
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const value = {
    user,
    loading,
    authRequired,
    unauthorized,
    isAuthenticated: user !== null,
    login,
    logout,
    checkAuthStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
